import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import BillModuleMessage, {
  EBillModuleMessageType,
} from '@/components/ui/BillModule/BillModuleMessage/BillModuleMessage';
import { getIsUnavailable } from '@/redux/selectors/listing/availability';
import {
  getDiscountMessage,
  getIsRequestLessThanMinimumDays,
  getQuoteFromAndTo,
} from '@/redux/selectors/listing/bill';
import { getListingMinimumDays } from '@/redux/selectors/listing/page';
import { getQuoteData, getQuoteError } from '@/redux/selectors/quote';

import css from './BillSectionNotificationsContainer.module.css';

export const BillSectionNotificationsContainer: React.FC = () => {
  const intl = useIntl();

  const { from, to } = useSelector(getQuoteFromAndTo);

  const quoteError = useSelector(getQuoteError);
  const isUnavailable = useSelector(getIsUnavailable);
  const minimumDays = useSelector(getListingMinimumDays);
  const isRequestLessThanMinimumDays = useSelector(getIsRequestLessThanMinimumDays);
  const discountMessage = useSelector(getDiscountMessage);
  const quote = useSelector(getQuoteData);
  const hasDatesSelected = Boolean(from && to);

  const dayTo = dayjs(to);
  const isOver12Months = dayjs().add(12, 'month').isBefore(dayTo);

  const message = isOver12Months
    ? intl.formatMessage({
        defaultMessage:
          'Bookings more than 1 year in advance must be created by the Host. Please contact Host',
        id: 'B2AbyB',
        description: 'Listing Page > Bill Module > Over 12 months warning',
      })
    : intl.formatMessage({
        defaultMessage: 'Your dates are not available',
        id: 'l7UBMY',
        description: 'Listing Page > Bill Module',
      });

  const errorMessage = quoteError || (isUnavailable ? message : undefined);

  const shouldShowWarning = useMemo(() => {
    if (!minimumDays) {
      return false;
    }

    const numberOfDays = dayjs(to).diff(dayjs(from), 'day');
    const difference = Number(minimumDays - numberOfDays);

    if (difference > 0 && to && from && !isUnavailable) {
      return true;
    }

    return false;
  }, [from, to, isUnavailable, minimumDays]);

  const shouldShowMinimumDaysWarning = Boolean(
    minimumDays && hasDatesSelected && shouldShowWarning && !isRequestLessThanMinimumDays,
  );

  const shouldShowRequestLessThanMinimumDaysWarning = Boolean(isRequestLessThanMinimumDays);

  return (
    <>
      {!!errorMessage && (
        <BillModuleMessage
          message={errorMessage}
          type={EBillModuleMessageType.Error}
          className={css.message}
        />
      )}
      {shouldShowMinimumDaysWarning && (
        <BillModuleMessage
          message={quote?.warning?.message || ''}
          type={EBillModuleMessageType.Error}
          className={css.message}
        />
      )}
      {shouldShowRequestLessThanMinimumDaysWarning && (
        <BillModuleMessage
          message={quote?.warning?.message || ''}
          type={EBillModuleMessageType.Default}
          className={css.message}
        />
      )}
      {!shouldShowWarning && !shouldShowRequestLessThanMinimumDaysWarning && discountMessage && (
        <BillModuleMessage
          message={discountMessage || ''}
          type={EBillModuleMessageType.Default}
          className={css.message}
        />
      )}
    </>
  );
};
